import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
  inject
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ROLES } from 'src/app/features/authentication/authRoles.constants';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { AuthenticationService } from 'src/app/features/authentication/services/authentication.service';
import { FeatureflagsService } from 'src/app/shared/services/featureflags.service';
import { FEATURE_FLAGS } from 'src/app/shared/constants/constants';
import { hasRolePermission } from 'src/app/features/user-management/utils/UserSetupUtils';
import { DealerState } from 'src/app/shared/state/DealerState';
import { Store } from '@ngxs/store';

export interface SetupTab {
  labelKey: string;
  fallbackLabel: string;
  link: string;
  visible: () => boolean;
}

@Component({
  selector: 'app-setup-container',
  templateUrl: './setup-container.component.html',
  styleUrls: ['./setup-container.component.scss'],
  standalone: false
})
export class SetupContainerComponent implements OnInit, AfterViewInit, OnDestroy {
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private authService = inject(AuthenticationService);
  private featureflagsService = inject(FeatureflagsService);
  private store = inject(Store);
  public translationService = inject(TranslationService);

  readonly ROLES = ROLES;

  @ViewChild('navContainer', { read: ElementRef }) navContainerRef: ElementRef;
  @ViewChildren('shadowLink', { read: ElementRef }) shadowLinks: QueryList<ElementRef>;
  @ViewChild('shadowMoreLink', { read: ElementRef }) shadowMoreLinkRef: ElementRef;

  private resizeObserver: ResizeObserver;
  private rafHandle: number | null = null;
  private postFrameTimer: ReturnType<typeof setTimeout> | null = null;
  private routerSub: Subscription;
  private posSub: Subscription;
  private readonly onWindowResize = () => this.scheduleRecalculation();
  private zoomMq: MediaQueryList | null = null;
  private posEnabled = false;

  displayedTabs: SetupTab[] = [];
  overflowTabs: SetupTab[] = [];

  private _permittedTabs: SetupTab[] | null = null;
  private _tabOrder: SetupTab[] | null = null;
  private readonly tabWidthMap = new Map<string, number>();

  private readonly allTabs: SetupTab[] = [
    {
      labelKey: 'pointOfSaleText',
      fallbackLabel: 'Point of Sale',
      link: '/home/setup/pointOfSale',
      visible: () => this.canViewPosTab()
    },
    {
      labelKey: 'emailSetupText',
      fallbackLabel: 'Email Setup',
      link: '/home/setup/emailSetup',
      visible: () => this.checkTokenForAuthority(ROLES.manage_emails)
    },
    {
      labelKey: 'plantSetupText',
      fallbackLabel: 'Plant Setup',
      link: '/home/setup/plantSetup',
      visible: () => this.checkTokenForAuthority(ROLES.plant_view)
    },
    {
      labelKey: 'productImportText',
      fallbackLabel: 'Product Import',
      link: '/home/setup/productImport',
      visible: () => this.checkTokenForAuthority(ROLES.edit_product)
    },
    {
      labelKey: 'siteSetupText',
      fallbackLabel: 'Site Setup',
      link: '/home/setup/siteSetup',
      visible: () => this.checkTokenForAuthority(ROLES.site_view)
    },
    {
      labelKey: 'dealerSetupText',
      fallbackLabel: 'Dealer Setup',
      link: '/home/setup/dealerSetup',
      visible: () => this.canEditDealerSetup()
    },
    {
      labelKey: 'zoneSetupText',
      fallbackLabel: 'Zone Setup',
      link: '/home/setup/zoneSetup',
      visible: () => this.checkTokenForAuthority(ROLES.view_zone)
    },
    {
      labelKey: 'usersAndGroupsText',
      fallbackLabel: 'Users & Groups',
      link: '/home/setup/usersetup',
      visible: () => this.canViewUserSetupTab()
    }
  ];

  get permittedTabs(): SetupTab[] {
    if (!this._permittedTabs) {
      this._permittedTabs = this.allTabs
        .filter((tab) => tab.visible())
        .map((tab) =>
          tab.link === '/home/setup/usersetup'
            ? {
                ...tab,
                link:
                  this.checkTokenForAuthority(ROLES.view_user) || this.checkTokenForAuthority(ROLES.edit_user)
                    ? '/home/setup/usersetup'
                    : '/home/setup/usersetup/user-group'
              }
            : tab
        )
        // Display tabs (and therefore the default/first-selected tab) in alphabetical order by label
        // so the tab bar order stays consistent regardless of declaration order in `allTabs`.
        .sort((a, b) => this.getLabel(a).localeCompare(this.getLabel(b)));
    }
    return this._permittedTabs;
  }

  get tabOrder(): SetupTab[] {
    if (!this._tabOrder) {
      this._tabOrder = [...this.permittedTabs];
    }
    return this._tabOrder;
  }

  get isMoreTabActive(): boolean {
    const url = this.router.url;
    return this.overflowTabs.some((tab) => url.startsWith(tab.link));
  }

  getLabel(tab: SetupTab): string {
    const translated = this.translationService.translate(tab.labelKey);
    // translationService.translate() returns the key itself when no translation exists for it
    // (e.g. 'productImportText' has no entry yet) — fall back to the readable label in that case.
    return translated && translated !== tab.labelKey ? translated : tab.fallbackLabel;
  }

  getMoreLabel(): string {
    const translated = this.translationService.translate('moreText');
    return translated && translated !== 'moreText' ? translated : 'More';
  }

  ngOnInit(): void {
    this.posEnabled = this.store.selectSnapshot(DealerState.posEnabled) === true;
    this.posSub = this.store.select(DealerState.posEnabled).subscribe((posEnabled) => {
      this.posEnabled = posEnabled === true;
      this.resetTabCache();
      this.navigateToFirstPermittedTabIfNeeded();
      this.calculateVisibleTabs();
      this.cdr.detectChanges();
    });
    this.navigateToFirstPermittedTabIfNeeded();

    this.routerSub = this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.navigateToFirstPermittedTabIfNeeded();
      this.calculateVisibleTabs();
      this.cdr.detectChanges();
      this.scrollActiveTabIntoView();
    });
  }

  ngAfterViewInit(): void {
    this.buildTabWidthMap();
    this.calculateVisibleTabs();

    this.ngZone.runOutsideAngular(() => {
      this.resizeObserver = new ResizeObserver(() => this.scheduleRecalculation());
      if (this.navContainerRef?.nativeElement) {
        this.resizeObserver.observe(this.navContainerRef.nativeElement);
      }
      this.resizeObserver.observe(document.documentElement);

      this.setupZoomWatcher();
      window.addEventListener('resize', this.onWindowResize);
    });
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.teardownZoomWatcher();
    window.removeEventListener('resize', this.onWindowResize);
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    if (this.postFrameTimer !== null) clearTimeout(this.postFrameTimer);
    this.routerSub?.unsubscribe();
    this.posSub?.unsubscribe();
  }

  onOverflowTabSelected(selectedTab: SetupTab): void {
    const order = [...this.tabOrder];
    const lastVisibleIdx = this.displayedTabs.length - 1;
    const selectedIdx = order.findIndex((t) => t.link === selectedTab.link);

    if (lastVisibleIdx < 0 || selectedIdx < 0 || selectedIdx <= lastVisibleIdx) return;

    [order[lastVisibleIdx], order[selectedIdx]] = [order[selectedIdx], order[lastVisibleIdx]];
    this._tabOrder = order;

    this.calculateVisibleTabs();
    this.cdr.detectChanges();
  }

  private canEditDealerSetup(): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(ROLES.edit_dealer_setup);
  }

  private canViewPosTab(): boolean {
    return (
      this.posEnabled &&
      this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.POS_INTERFACE_ENABLED) &&
      this.checkTokenForAuthority(ROLES.view_pos_interface)
    );
  }

  private navigateToFirstPermittedTabIfNeeded(): void {
    if (!this.isSetupRootUrl(this.router.url)) return;

    const first = this.permittedTabs[0];
    if (first && this.router.url !== first.link) {
      this.router.navigateByUrl(first.link);
    }
  }

  private isSetupRootUrl(url: string): boolean {
    const path = url.split(/[?#]/, 1)[0];
    return path === '/home/setup' || path === '/home/setup/';
  }

  private resetTabCache(): void {
    this._permittedTabs = null;
    this._tabOrder = null;
    this.tabWidthMap.clear();
  }

  private canViewUserSetupTab(): boolean {
    const isUserMgmtEnabled = this.featureflagsService.getFeatureFlagValue(FEATURE_FLAGS.USER_MGMT_ENABLED);
    return (
      isUserMgmtEnabled &&
      (hasRolePermission(ROLES.view_user) || hasRolePermission(ROLES.view_user_group) || hasRolePermission(ROLES.edit_user))
    );
  }

  private checkTokenForAuthority(tokenName: string): boolean {
    const roles = this.authService.getRolesFromToken();
    return roles.includes(tokenName);
  }

  private setupZoomWatcher(): void {
    this.teardownZoomWatcher();
    this.zoomMq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    this.zoomMq.addEventListener('change', this.onZoomChange);
  }

  private teardownZoomWatcher(): void {
    if (this.zoomMq) {
      this.zoomMq.removeEventListener('change', this.onZoomChange);
      this.zoomMq = null;
    }
  }

  private readonly onZoomChange = (): void => {
    this.setupZoomWatcher();
    this.scheduleRecalculation();
  };

  private scheduleRecalculation(): void {
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    if (this.postFrameTimer !== null) clearTimeout(this.postFrameTimer);

    this.rafHandle = requestAnimationFrame(() => {
      this.rafHandle = null;
      this.postFrameTimer = setTimeout(() => {
        this.postFrameTimer = null;
        this.ngZone.run(() => {
          this.calculateVisibleTabs();
          this.cdr.detectChanges();
          this.scrollActiveTabIntoView();
        });
      }, 0);
    });
  }

  private scrollActiveTabIntoView(): void {
    setTimeout(() => {
      const activeLink = this.navContainerRef?.nativeElement?.querySelector('.mat-mdc-tab-link.mdc-tab--active');
      if (activeLink) {
        activeLink.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
    }, 50);
  }

  private buildTabWidthMap(): void {
    const tabs = this.permittedTabs;
    const els = this.shadowLinks?.toArray() ?? [];
    tabs.forEach((tab, i) => {
      const w = els[i]?.nativeElement?.offsetWidth;
      if (w) this.tabWidthMap.set(tab.link, w);
    });
  }

  private calculateVisibleTabs(): void {
    const allTabs = this.tabOrder;

    if (!this.navContainerRef?.nativeElement) {
      this.displayedTabs = allTabs;
      this.overflowTabs = [];
      return;
    }

    if (this.tabWidthMap.size === 0) {
      this.buildTabWidthMap();
    }

    const containerWidth = Math.max(1, this.navContainerRef.nativeElement.getBoundingClientRect().width);
    const moreWidth = this.shadowMoreLinkRef?.nativeElement?.offsetWidth ?? 104;

    const currentUrl = this.router.url;
    const activeTabIndex = allTabs.findIndex((tab) => currentUrl.startsWith(tab.link));

    let totalWidth = 0;
    let splitIndex = allTabs.length;

    for (let i = 0; i < allTabs.length; i++) {
      const tabWidth = this.tabWidthMap.get(allTabs[i].link) ?? 120;
      const isLast = i === allTabs.length - 1;
      const required = totalWidth + tabWidth + (isLast ? 0 : moreWidth);

      if (required > containerWidth) {
        splitIndex = i;
        break;
      }
      totalWidth += tabWidth;
    }

    if (allTabs.length > 0) splitIndex = Math.max(1, splitIndex);

    if (activeTabIndex >= 0 && activeTabIndex >= splitIndex && splitIndex > 0) {
      const reorderedTabs = [...allTabs];
      const activeTab = reorderedTabs[activeTabIndex];
      reorderedTabs.splice(activeTabIndex, 1);
      reorderedTabs.splice(splitIndex - 1, 0, activeTab);
      this._tabOrder = reorderedTabs;
      this.displayedTabs = reorderedTabs.slice(0, splitIndex);
      this.overflowTabs = reorderedTabs.slice(splitIndex);
    } else {
      this.displayedTabs = allTabs.slice(0, splitIndex);
      this.overflowTabs = allTabs.slice(splitIndex);
    }
  }
}
